import { motion } from "framer-motion";
import { ChevronUp, Heart, MoreVertical, Pause, PictureInPicture2, Play, Vibrate, VibrateOff } from "lucide-react";
import React, { useState } from "react";

interface PlayerTrackInfoProps {
  title: string;
  artist: string;
  albumArt?: string;
  isPlaying: boolean;
  isLiked: boolean;
  isHapticsSupported: boolean;
  isHapticsEnabled: boolean;
  onLikeToggle: () => void;
  onHapticsToggle: () => void;
  onPlayPause: () => void;
  onExpand: () => void;
  onWindowedMode: () => void;
  onOpenMenu: () => void;
  onHapticPresetOpen: () => void;
  mini?: boolean;
}

export const PlayerTrackInfo: React.FC<PlayerTrackInfoProps> = ({
  title,
  artist,
  isPlaying,
  isLiked,
  isHapticsSupported,
  isHapticsEnabled,
  onLikeToggle,
  onHapticsToggle,
  onPlayPause,
  onExpand,
  onWindowedMode,
  onOpenMenu,
  onHapticPresetOpen,
  mini = false,
}) => {
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isLongPress, setIsLongPress] = useState(false);

  const handleHapticsButtonPress = () => {
    setIsLongPress(false);
    const timer = setTimeout(() => {
      setIsLongPress(true);
      onHapticPresetOpen();
    }, 500);
    setLongPressTimer(timer);
  };

  const handleHapticsButtonRelease = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
      if (!isLongPress) {
        onHapticsToggle();
      }
    }
  };

  const HapticsButton = ({ size }: { size: "small" | "large" }) => (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onMouseDown={handleHapticsButtonPress}
      onMouseUp={handleHapticsButtonRelease}
      onMouseLeave={handleHapticsButtonRelease}
      onTouchStart={handleHapticsButtonPress}
      onTouchEnd={handleHapticsButtonRelease}
      className={`flex p-2 rounded-full transition ${
        isHapticsEnabled ? "text-yellow-500" : "text-zinc-400 hover:text-white"
      }`}
    >
      {isHapticsEnabled ? (
        <Vibrate className={`w-${size === "small" ? "5" : "6"} h-${size === "small" ? "5" : "6"}`} />
      ) : (
        <VibrateOff className={`w-${size === "small" ? "5" : "6"} h-${size === "small" ? "5" : "6"}`} />
      )}
    </motion.button>
  );

  if (mini) {
    return (
      <div className="flex items-center justify-end gap-4 w-full">
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onLikeToggle}
            className={`p-2 rounded-full transition ${isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"}`}
          >
            <Heart className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} />
          </motion.button>
          {isHapticsSupported && <HapticsButton size="small" />}
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onPlayPause}
            className="p-2 rounded-full bg-white hover:bg-zinc-200 transition"
          >
            {isPlaying ? <Pause className="w-5 h-5 text-black" /> : <Play className="w-5 h-5 text-black" />}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onWindowedMode}
            className="hidden md:block p-2 pl-4 text-zinc-400 hover:text-white transition"
          >
            <PictureInPicture2 className="w-5 h-5" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onExpand}
            className="hidden md:block p-2 text-zinc-400 hover:text-white transition"
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <div>
          <motion.h3 layoutId="title" className="text-2xl font-bold mb-1">
            {title}
          </motion.h3>
          <motion.p layoutId="artist" className="text-zinc-400">
            {artist}
          </motion.p>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onLikeToggle}
            className={`p-2 rounded-full transition ${isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"}`}
          >
            <Heart className="w-6 h-6" />
          </motion.button>

          {isHapticsSupported && <HapticsButton size="large" />}

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onOpenMenu}
            className="p-2 text-zinc-400 hover:text-white transition"
          >
            <MoreVertical className="w-6 h-6" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};
